import { useEffect, useRef, useState } from 'react';

const HERO_IMAGE = 'https://public.readdy.ai/ai/img_res/949575e51fe35d0d41251c9145ecd55f.jpg';
const DETAIL_IMAGE = 'https://readdy.ai/api/search-image?query=extreme%20close%20up%20detail%20macro%20photography%20premium%20dark%20charcoal%20architectural%20asphalt%20shingles%20freshly%20installed%20perfect%20alignment%20texture%20quality%20roofing%20craftsmanship%20detail%20shot%20editorial%20quality&width=600&height=380&seq=why_v4_detail_01&orientation=landscape';

const REASONS = [
  { icon: 'ri-medal-2-fill', title: 'GAF & Fortification Certified', desc: 'The highest industry certifications for quality and storm resistance — on every project.' },
  { icon: 'ri-group-fill', title: 'Experienced Local Crew', desc: 'We know Gulf Coast weather firsthand. We build roofs specifically engineered to handle it.' },
  { icon: 'ri-hammer-fill', title: 'Premium Materials Only', desc: 'High-grade products from trusted manufacturers on every project. Zero shortcuts.' },
  { icon: 'ri-file-list-3-fill', title: 'Honest, Transparent Pricing', desc: 'No hidden fees. No vague estimates. A clear breakdown before any work starts.' },
  { icon: 'ri-sparkling-2-fill', title: 'Clean, Efficient Job Sites', desc: 'We respect your property. Every site left cleaner than we found it — guaranteed.' },
  { icon: 'ri-star-fill', title: '5-Star Google Rated', desc: 'Trusted by homeowners and businesses across the Gulf Coast. Real reviews, real results.' },
];

export default function WhyChooseUs() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="py-24 md:py-32 bg-cream overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-14 xl:gap-20 items-start">

          {/* Left — image composition */}
          <div className={`w-full lg:w-[460px] shrink-0 relative transition-all duration-900 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
            style={{ transitionDuration: '900ms' }}>

            {/* Main portrait */}
            <div className="relative w-full" style={{ paddingBottom: '10px' }}>
              <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden">
                <img src={HERO_IMAGE} alt="All Done Roofing LLC" className="w-full h-full object-cover object-top" />
                {/* Subtle overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/20 to-transparent"></div>
                <div className="absolute inset-0 ring-1 ring-gold/15 rounded-lg pointer-events-none"></div>
              </div>

              {/* Detail image — bottom right overlap */}
              <div className="absolute -bottom-4 -right-5 w-52 h-36 rounded-lg overflow-hidden border-[3px] border-cream hidden md:block">
                <img src={DETAIL_IMAGE} alt="Premium shingle detail" className="w-full h-full object-cover" />
                <div className="absolute inset-0 ring-1 ring-gold/20 rounded-sm pointer-events-none"></div>
              </div>

              {/* Cert badge — top left overlap */}
              <div className="absolute top-8 -left-5 bg-ink rounded-lg px-5 py-4 border border-gold/25 hidden md:block">
                <i className="ri-medal-2-fill text-gold text-lg mb-1 block"></i>
                <p className="font-condensed font-bold text-[11px] text-white uppercase tracking-wide">GAF Certified</p>
                <p className="font-condensed text-[10px] text-white/45 uppercase tracking-wide">Fortification</p>
              </div>

              {/* Google rating badge */}
              <div className="absolute -right-4 top-1/3 bg-white rounded-lg px-4 py-3 border border-gray-100 hidden lg:block">
                <div className="flex items-center gap-1 mb-1">
                  {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-gold text-[10px]"></i>)}
                </div>
                <p className="font-bold text-charcoal text-xs">5.0 Stars</p>
                <p className="text-gray-400 text-[10px]">Google Reviews</p>
              </div>
            </div>
          </div>

          {/* Right — content */}
          <div className={`flex-1 transition-all duration-900 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
            style={{ transitionDuration: '900ms', transitionDelay: '200ms' }}>

            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-gold"></span>
              <span className="text-[11px] font-bold tracking-super uppercase text-gold">Why Choose Us</span>
            </div>
            <h2 className="font-condensed font-bold text-5xl md:text-6xl text-charcoal uppercase leading-none mb-3">
              Built on Quality,<br />
              <span className="font-serif font-normal italic text-gray-400 text-4xl md:text-5xl normal-case leading-tight">
                Not Shortcuts
              </span>
            </h2>
            <div className="w-10 h-px bg-gold/40 my-6"></div>
            <p className="text-gray-500 text-base leading-relaxed font-light mb-10 max-w-lg">
              When it comes to protecting your home or business, the right roofing contractor makes all the difference. At <strong className="text-charcoal font-semibold">All Done Roofing LLC</strong>, we deliver precision craftsmanship, proven materials, and genuine service across Gulfport, Biloxi, Ocean Springs, and the entire Gulf Coast.
            </p>

            {/* Reasons grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
              {REASONS.map((r, i) => (
                <div key={i}
                  className={`flex items-start gap-4 group transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: `${300 + i * 80}ms` }}>
                  <div className="w-9 h-9 flex items-center justify-center shrink-0 border border-gold/30 rounded group-hover:bg-gold/8 transition-colors">
                    <i className={`${r.icon} text-gold text-sm`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal text-sm mb-0.5">{r.title}</p>
                    <p className="text-gray-500 text-xs leading-relaxed font-light">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#estimate"
                onClick={e => { e.preventDefault(); document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center justify-center gap-3 bg-charcoal text-white font-bold text-[12px] tracking-widest uppercase px-8 py-4 rounded hover:bg-ink transition-colors cursor-pointer whitespace-nowrap">
                Request a Free Estimate
                <i className="ri-arrow-right-line text-gold"></i>
              </a>
              <a href="tel:2285473887"
                className="inline-flex items-center justify-center gap-3 border border-charcoal/15 text-charcoal font-semibold text-[12px] tracking-widest uppercase px-8 py-4 rounded hover:border-gold/40 hover:text-gold transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-gold text-sm"></i>
                (228) 547-3887
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
